import { get_depret_from_result } from "./LDSHelpers";
import { pickOrganizedTourDealSpread } from "./pickOrganizedTourDealSpread";

/**
 * Parse LDS / search date strings → { y, m, d } or null.
 * Supports: DD/MM/YYYY[ HH:mm], YYYY-MM-DD, DD-MM-YYYY
 * @param {unknown} raw
 */
export function parseOrganizedTourDateInput(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return null;

  const datePart = s.split(/\s+/)[0];
  let m;
  let d;
  let y;

  if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    [y, m, d] = datePart.split("-").map(Number);
  } else if (/^\d{1,2}[/.-]\d{1,2}[/.-]\d{4}$/.test(datePart)) {
    const parts = datePart.split(/[/.-]/);
    const a = Number(parts[0]);
    const b = Number(parts[1]);
    const c = Number(parts[2]);
    if (parts[0].length === 4) {
      y = a;
      m = b;
      d = c;
    } else {
      d = a;
      m = b;
      y = c;
    }
  } else {
    return null;
  }

  if (!y || !m || !d || m < 1 || m > 12 || d < 1 || d > 31) return null;
  return { y, m, d };
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

/**
 * @param {{ y: number, m: number, d: number } | null} parts
 * @param {"slash"|"dot"} style
 */
export function formatOrganizedTourDateParts(parts, style = "slash") {
  if (!parts) return "";
  const sep = style === "dot" ? "." : "/";
  return `${pad2(parts.d)}${sep}${pad2(parts.m)}${sep}${parts.y}`;
}

/**
 * @param {unknown} result — raw search row or normalized object with raw/dealRoot
 * @returns {{
 *   show: boolean,
 *   depRaw: string,
 *   retRaw: string,
 *   depFormatted: string,
 *   retFormatted: string,
 *   displayRange: string,
 *   displayCompact: string,
 *   depSource: string,
 *   retSource: string,
 * }}
 */
export function resolveOrganizedTourDateRange(result) {
  const root = pickOrganizedTourDealSpread(result);
  const tour = root.tour || result?.tour || {};
  const searchDate = result?.searchDate || root.searchDate;

  const depCandidates = [
    ["deal.depDate", root.depDate],
    ["deal.departureDate", root.departureDate],
    ["tour.depDate", tour.depDate],
    ["raw.depDate", result?.depDate],
    ["raw.dep_date", result?.dep_date],
    ["searchDate.depDate", searchDate?.depDate],
    ["searchDate.departureDate", searchDate?.departureDate],
    ["dealRoot.depDate", result?.dealRoot?.depDate],
  ];

  const retCandidates = [
    ["deal.retDate", root.retDate],
    ["deal.returnDate", root.returnDate],
    ["tour.retDate", tour.retDate],
    ["raw.retDate", result?.retDate],
    ["raw.ret_date", result?.ret_date],
    ["searchDate.retDate", searchDate?.retDate],
    ["searchDate.returnDate", searchDate?.returnDate],
    ["dealRoot.retDate", result?.dealRoot?.retDate],
  ];

  let depRaw = "";
  let depSource = "none";
  for (const [source, val] of depCandidates) {
    const t = String(val ?? "").trim();
    if (t) {
      depRaw = t;
      depSource = source;
      break;
    }
  }

  let retRaw = "";
  let retSource = "none";
  for (const [source, val] of retCandidates) {
    const t = String(val ?? "").trim();
    if (t) {
      retRaw = t;
      retSource = source;
      break;
    }
  }

  if (!depRaw && root.linkedFlights) {
    const fromFlights = get_depret_from_result(root);
    if (fromFlights?.depDate) {
      depRaw = String(fromFlights.depDate).trim();
      depSource = "linkedFlights.flight[0].leg.depDate";
    }
    if (fromFlights?.retDate) {
      retRaw = String(fromFlights.retDate).trim();
      retSource = "linkedFlights.flight[1].leg.depDate";
    }
  }

  const depParts = parseOrganizedTourDateInput(depRaw);
  const retParts = parseOrganizedTourDateInput(retRaw);
  const depFormatted = formatOrganizedTourDateParts(depParts, "slash");
  const retFormatted = formatOrganizedTourDateParts(retParts, "slash");

  let displayRange = "";
  let displayCompact = "";
  if (depFormatted && retFormatted) {
    displayRange = `${depFormatted} → ${retFormatted}`;
    if (depParts && retParts && depParts.y === retParts.y) {
      displayCompact = `${pad2(depParts.d)}.${pad2(depParts.m)} – ${pad2(retParts.d)}.${pad2(retParts.m)}.${retParts.y}`;
    } else {
      displayCompact = displayRange;
    }
  } else if (depFormatted) {
    displayRange = depFormatted;
    displayCompact = depFormatted;
  }

  const out = {
    show: Boolean(depFormatted),
    depRaw,
    retRaw,
    depFormatted,
    retFormatted,
    displayRange,
    displayCompact,
    depSource,
    retSource,
  };

  if (import.meta.env?.DEV) {
    console.log("[ORG_TOUR_DATE_RANGE_RESOLVE]", {
      dealId: root.dealId || result?.dealId,
      ...out,
    });
  }

  return out;
}
